import { useMemo, useCallback } from 'react';
import {
    Box, Flex, Text, Table, Thead, Tbody, Tr, Th, Td,
    Card, CardBody, StatArrow,
    IconButton, Tooltip, Icon, HStack, VStack, Badge
} from '@chakra-ui/react';
import { TbDownload } from 'react-icons/tb';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, Cell, LineChart, Line, PieChart, Pie, Legend, ReferenceLine
} from 'recharts';
import { AnalyticsResponse, OutputType, TableColumn } from '../../../types/analyticsChat';
import { getBarChartYDomain } from '../../../utils/chartUtils';
import { PRIMARY_CHART_COLOR } from '../shared/formatters';
import { calculateMetricChange, changeSuffix } from '../shared/metricChange';

const CHART_COLORS = [PRIMARY_CHART_COLOR, '#2DD0C6', '#3D8FEA', '#6BADF2', '#97C5F7', '#C0DCFA'];
const PIE_COLORS = ['#005BC4', '#00897B', '#F4511E', '#7B1FA2', '#1976D2', '#FFA000'];

interface ChatVisualizationProps {
    response: AnalyticsResponse;
}

const PERCENT_METRICS = [
    'avgGrossMarginPct', 'medianGrossMarginPct', 'avgGrossMarginPctWoPM',
    'medianGrossMarginPctWoPM', 'grossMarginLossFromPM', 'pctAutoOrdered',
    'pctAutoOrderEnabled', 'partListPricePctOfOE',
] as const;

const CURRENCY_METRICS = [
    'grossProfit', 'orderVolume', 'orderCost', 'avgListPrice', 'medianListPrice',
    'avgOrderSize', 'medianOrderSize', 'missedOpportunity', 'avgMissedOpportunity',
    'avgListPriceWoPM', 'medianListPriceWoPM',
] as const;

// Format metric names for display
const formatMetricName = (metric: string): string => {
    const names: Record<string, string> = {
        grossProfit: 'Gross Profit',
        orderVolume: 'Order Volume',
        orderCost: 'Order Cost',
        orderCount: 'Orders',
        partsOrdered: 'Parts',
        avgGrossMarginPct: 'Avg Margin %',
        medianGrossMarginPct: 'Median Margin %',
        avgListPrice: 'Avg List Price',
        medianListPrice: 'Median List Price',
        avgOrderSize: 'Avg Order Size',
        medianOrderSize: 'Median Order Size',
        missedOpportunity: 'Missed Opportunity',
        avgMissedOpportunity: 'Avg. Missed Opportunity',
        pctAutoOrdered: '% Auto Ordered',
        pctAutoOrderEnabled: '% Auto Enabled',
        avgGrossMarginPctWoPM: 'Avg. Margin (w/o PM)',
        grossMarginLossFromPM: 'Margin Loss (PM)',
        avgListPriceWoPM: 'Avg. Price (w/o PM)',
    };
    return names[metric] || metric;
};

// Format values based on metric type
const formatMetricValue = (value: number | null | undefined, metric: string): string => {
    if (value === null || value === undefined) return '—';

    if ((PERCENT_METRICS as readonly string[]).includes(metric)) {
        return `${value.toFixed(1)}%`;
    }
    
    if ((CURRENCY_METRICS as readonly string[]).includes(metric)) {
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    return value.toLocaleString();
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box bg="white" p={3} borderRadius="md" boxShadow="none" border="1px solid" borderColor="gray.200">
                <Text fontWeight="semibold" mb={1}>{label}</Text>
                {payload.map((entry: any, index: number) => (
                    <Text key={index} color={entry.color} fontSize="sm">
                        {entry.name}: {formatMetricValue(entry.value, entry.dataKey)}
                    </Text>
                ))}
            </Box>
        );
    }
    return null;
};

// Single stat card for "single" output type
const SingleStatCard = ({ response }: { response: AnalyticsResponse }) => {
    const item = response.data[0] as Record<string, any>;
    const primaryMetric = response.meta.metrics[0];
    
    if (!item) return null;

    const value = item[primaryMetric] as number;
    const priorData = item.prior as Record<string, any> | undefined;
    const prior = priorData?.[primaryMetric] as number | undefined;
    const backendChange = item.change?.[primaryMetric];
    const isPercent = (PERCENT_METRICS as readonly string[]).includes(primaryMetric);
    const change = typeof backendChange === 'number'
        ? backendChange
        : calculateMetricChange(value, prior, isPercent);
    const changeLabel = changeSuffix(isPercent);
    const showComparison = response.meta.hasComparison;

    return (
        <Card bg="white" borderRadius="xl" boxShadow="none">
            <CardBody py={6} px={8}>
                <VStack spacing={1} align="center">
                    <Text fontSize="lg" color="gray.600" fontWeight="medium">
                        {item.name}
                    </Text>
                    <Text fontSize="4xl" fontWeight="bold" color="#005BC4">
                        {formatMetricValue(value, primaryMetric)}
                    </Text>
                    <Text fontSize="sm" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                        {formatMetricName(primaryMetric)}
                    </Text>
                    {change !== undefined && (
                        <HStack mt={2}>
                            <StatArrow type={change >= 0 ? 'increase' : 'decrease'} />
                            <Text fontSize="sm" color={change >= 0 ? 'green.500' : 'red.500'}>
                                {Math.abs(change).toFixed(1)}{changeLabel} from prior period
                            </Text>
                        </HStack>
                    )}
                    {showComparison && change === undefined && (
                        <Text fontSize="sm" color="gray.400" mt={2}>—</Text>
                    )}
                </VStack>
            </CardBody>
        </Card>
    );
};

// Horizontal bar chart for rankings
const RankingBarChart = ({ response }: { response: AnalyticsResponse }) => {
    const primaryMetric = response.meta.metrics[0];
    const data = response.data.slice(0, 10);

    const domainFormat = (PERCENT_METRICS as readonly string[]).includes(primaryMetric)
        ? 'percent'
        : (CURRENCY_METRICS as readonly string[]).includes(primaryMetric)
            ? 'currency'
            : 'number';

    const vals = data
        .map((d) => Number((d as Record<string, unknown>)[primaryMetric]) || 0)
        .filter((v) => !isNaN(v));
    const dMin = vals.length ? Math.min(...vals) : 0;
    const dMax = vals.length ? Math.max(...vals) : 0;
    const [xMin, xMax] = getBarChartYDomain(dMin, dMax, {
        format: domainFormat,
        capPercentMaxAt100: false,
    });

    return (
        <Box h="300px" w="100%">
            <ResponsiveContainer>
                <BarChart data={data} layout="vertical" margin={{ left: 20, right: 52, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis
                        type="number"
                        domain={[xMin, xMax]}
                        tickFormatter={(v) => formatMetricValue(v, primaryMetric)}
                        allowDecimals={domainFormat === 'percent'}
                    />
                    <YAxis
                        dataKey="name"
                        type="category"
                        width={120}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    {xMin < 0 && <ReferenceLine x={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                    <Bar dataKey={primaryMetric} name={formatMetricName(primaryMetric)} radius={[0, 4, 4, 0]}>
                        {data.map((_, index) => (
                            <Cell key={index} fill={PRIMARY_CHART_COLOR} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

// Line chart for timeseries
const TimeseriesLineChart = ({ response }: { response: AnalyticsResponse }) => {
    const primaryMetric = response.meta.metrics[0];
    
    // Group data by domain for multiple lines
    const domains = useMemo(() => {
        const domainSet = new Set<string>();
        response.data.forEach((item: any) => {
            if (item.domainName) domainSet.add(item.domainName);
        });
        return Array.from(domainSet).slice(0, 6); // Max 6 lines
    }, [response.data]);

    // Transform data for line chart
    const chartData = useMemo(() => {
        const dateMap = new Map<string, any>();
        
        response.data.forEach((item: any) => {
            const date = item.date;
            if (!dateMap.has(date)) {
                dateMap.set(date, { date });
            }
            const entry = dateMap.get(date);
            if (item.domainName) {
                entry[item.domainName] = item[primaryMetric];
            }
        });

        return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    }, [response.data, primaryMetric]);

    return (
        <Box h="300px" w="100%">
            <ResponsiveContainer>
                <LineChart data={chartData} margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tickFormatter={(v) => formatMetricValue(v, primaryMetric)} />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    {domains.map((domain, index) => (
                        <Line
                            key={domain}
                            type="monotone"
                            dataKey={domain}
                            name={domain}
                            stroke={CHART_COLORS[index % CHART_COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

// Pie chart for distribution
const DistributionPieChart = ({ response }: { response: AnalyticsResponse }) => {
    const primaryMetric = response.meta.metrics[0];
    
    const data = useMemo(() => {
        const dataItems = response.data as Array<Record<string, any>>;
        const total = dataItems.reduce((sum: number, item: Record<string, any>) => sum + ((item[primaryMetric] as number) || 0), 0);
        return dataItems.slice(0, 8).map((item: Record<string, any>) => ({
            name: item.name as string,
            value: item[primaryMetric] as number,
            percentage: total > 0 ? ((item[primaryMetric] as number) / total * 100).toFixed(1) : '0'
        }));
    }, [response.data, primaryMetric]);

    return (
        <Box h="100%" minH="300px" w="100%" display="flex" flexDirection="column">
            <Box flex={1} minH={0} w="100%">
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius="50%"
                        outerRadius="90%"
                        paddingAngle={2}
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        labelLine={{ stroke: '#666', strokeWidth: 1 }}
                    >
                        {data.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                    </Pie>
                    <RechartsTooltip
                        formatter={(value: number) => formatMetricValue(value, primaryMetric)}
                    />
                </PieChart>
            </ResponsiveContainer>
            </Box>
        </Box>
    );
};

// Data table component
const DataTable = ({ response }: { response: AnalyticsResponse }) => {
    const { data, meta } = response;
    const isTimeseries = meta.outputType === 'timeseries';
    
    const columns: TableColumn[] = useMemo(() => {
        const cols: TableColumn[] = [];
        
        // For timeseries, add date column first
        if (isTimeseries) {
            cols.push({ key: 'date', label: 'Date' });
        }
        
        // Add domain/name column
        cols.push({ 
            key: 'name', 
            label: meta.domain.charAt(0).toUpperCase() + meta.domain.slice(1) 
        });
        
        // Add metric columns
        meta.metrics.forEach(metric => {
            const isPercent = ['avgGrossMarginPct', 'medianGrossMarginPct', 'pctAutoOrdered', 'pctAutoOrderEnabled'].includes(metric);
            const isCurrency = ['grossProfit', 'orderVolume', 'orderCost', 'avgListPrice', 'missedOpportunity'].includes(metric);
            
            cols.push({
                key: metric,
                label: formatMetricName(metric),
                isNumeric: true,
                format: isPercent ? 'percent' : isCurrency ? 'currency' : 'number'
            });
        });
        
        return cols;
    }, [meta, isTimeseries]);

    // For timeseries, limit rows more since there can be many date x domain combinations
    const maxRows = isTimeseries ? 50 : 20;

    return (
        <Box overflowX="auto">
            <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                    <Tr>
                        {columns.map(col => (
                            <Th key={col.key} isNumeric={col.isNumeric} py={3}>
                                {col.label}
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {data.slice(0, maxRows).map((item, idx) => {
                        const rowData = item as Record<string, any>;
                        return (
                            <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                {columns.map(col => (
                                    <Td key={col.key} isNumeric={col.isNumeric}>
                                        {col.key === 'name' ? (
                                            <Text fontWeight="medium">
                                                {rowData.name || rowData.domainName || '—'}
                                            </Text>
                                        ) : col.key === 'date' ? (
                                            <Text fontSize="sm" color="gray.600">
                                                {new Date(rowData.date).toLocaleDateString('en-US', { 
                                                    month: 'short', 
                                                    day: 'numeric' 
                                                })}
                                            </Text>
                                        ) : (
                                            formatMetricValue(rowData[col.key] as number, col.key)
                                        )}
                                    </Td>
                                ))}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </Box>
    );
};

// CSV Export helper
const exportToCSV = (response: AnalyticsResponse) => {
    const { data, meta } = response;
    const isTimeseries = meta.outputType === 'timeseries';
    
    // Build headers based on output type
    const headers = isTimeseries
        ? ['Date', meta.domain.charAt(0).toUpperCase() + meta.domain.slice(1), ...meta.metrics.map(formatMetricName)]
        : [meta.domain.charAt(0).toUpperCase() + meta.domain.slice(1), ...meta.metrics.map(formatMetricName)];
    
    // Build rows
    const rows = data.map(item => {
        const rowData = item as Record<string, any>;
        const name = rowData.name || rowData.domainName || '';
        
        if (isTimeseries) {
            return [
                rowData.date,
                name,
                ...meta.metrics.map(m => rowData[m])
            ];
        }
        return [
            name,
            ...meta.metrics.map(m => rowData[m])
        ];
    });

    // Create CSV content
    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
            const cellStr = String(cell ?? '');
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
        }).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const outputTypeLabel = isTimeseries ? 'timeseries' : meta.outputType;
    link.setAttribute('download', `partbay_analytics_${meta.domain}_${outputTypeLabel}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Main visualization component
export const ChatVisualization = ({ response }: ChatVisualizationProps) => {
    const { meta } = response;
    const handleExport = useCallback(() => exportToCSV(response), [response]);

    // Choose visualization based on output type
    const renderVisualization = () => {
        switch (meta.outputType as OutputType) {
            case 'single':
                return <SingleStatCard response={response} />;
            case 'ranking':
            case 'comparison':
                return <RankingBarChart response={response} />;
            case 'timeseries':
                return <TimeseriesLineChart response={response} />;
            case 'distribution':
                return <DistributionPieChart response={response} />;
            default:
                return <RankingBarChart response={response} />;
        }
    };

    return (
        <VStack spacing={4} align="stretch" w="100%">
            {/* Chart */}
            {renderVisualization()}

            {/* Data table (for all except single) */}
            {meta.outputType !== 'single' && response.data.length > 0 && (
                <Box>
                    <Flex justify="space-between" align="center" mb={2}>
                        <HStack spacing={2}>
                            <Badge colorScheme="blue" variant="subtle">
                                {response.data.length} {response.data.length === 1 ? 'result' : 'results'}
                            </Badge>
                            <Text fontSize="xs" color="gray.500">
                                {meta.dateRange.start} to {meta.dateRange.end}
                            </Text>
                        </HStack>
                        <Tooltip label="Export to CSV" hasArrow>
                            <IconButton
                                aria-label="Export to CSV"
                                icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="#0071E3"
                                onClick={handleExport}
                                _hover={{ bg: 'blue.50' }}
                            />
                        </Tooltip>
                    </Flex>
                    <DataTable response={response} />
                </Box>
            )}

            {/* Export for single stat */}
            {meta.outputType === 'single' && (
                <Flex justify="flex-end">
                    <Tooltip label="Export to CSV" hasArrow>
                        <IconButton
                            aria-label="Export to CSV"
                            icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                            size="sm"
                            variant="ghost"
                            color="#0071E3"
                            onClick={handleExport}
                            _hover={{ bg: 'blue.50' }}
                        />
                    </Tooltip>
                </Flex>
            )}
        </VStack>
    );
};

export default ChatVisualization;

